import React, { useState, useRef, useEffect } from 'react'
import { X, Send, Loader2, Bug, MessageSquare, Rocket } from 'lucide-react'
import ChatMessage from './ChatMessage'
import ProviderSelector from './ProviderSelector'
import { useChat } from '../../hooks/useChat'
import { useProviders } from '../../hooks/useProviders'
import { useZenSettings } from '../../hooks/useZenSettings'

interface ChatAppProps {
  isOpen: boolean
  onClose: () => void
}

export const ChatApp: React.FC<ChatAppProps> = ({ isOpen, onClose }) => {
  const [message, setMessage] = useState('')
  const [localDebugMode, setLocalDebugMode] = useState(false)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLTextAreaElement>(null)
  
  const { messages, isLoading, sendMessage, clearMessages } = useChat()
  const { providers, currentProvider, switchProvider, isLoadingProviders, clearProviderSelection } = useProviders()
  const { settings, debugMode: zenDebugMode, getPreferredProvider } = useZenSettings()

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  useEffect(() => {
    scrollToBottom()
  }, [messages])

  useEffect(() => {
    if (isOpen) {
      inputRef.current?.focus()
      
      // Apply default provider from Zen settings if no provider is currently selected
      if (!currentProvider && providers.length > 0) {
        const preferredProvider = getPreferredProvider(providers)
        if (preferredProvider) {
          switchProvider(preferredProvider)
        }
      }
    }
  }, [isOpen, currentProvider, providers, getPreferredProvider, switchProvider])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!message.trim() || isLoading) return

    const userMessage = message.trim()
    setMessage('')
    // Use Zen debug mode setting or local override
    const effectiveDebugMode = zenDebugMode || localDebugMode
    await sendMessage(userMessage, currentProvider, effectiveDebugMode)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSubmit(e as any)
    }
    if (e.key === 'Escape') {
      onClose()
    }
  }

  const handleClear = () => {
    clearMessages()
    // Don't clear provider selection automatically - let users keep their preferred model
    // clearProviderSelection()
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="w-full max-w-4xl h-[80vh] mx-4 bg-white rounded-lg shadow-2xl border border-gray-200 flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center space-x-4">
            <h2 className="text-lg font-semibold text-gray-900">Chat Test</h2>
            <ProviderSelector
              providers={providers}
              currentProvider={currentProvider}
              onProviderChange={switchProvider}
              isLoading={isLoadingProviders}
            />
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setLocalDebugMode(!localDebugMode)}
              className={`flex items-center space-x-1 px-3 py-1.5 text-sm rounded-md transition-colors ${
                zenDebugMode || localDebugMode 
                  ? 'bg-orange-100 text-orange-800 hover:bg-orange-200' 
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-200'
              }`}
              title={zenDebugMode ? 'Debug mode enabled in Zen Settings' : 'Toggle debug mode'}
            >
              {zenDebugMode || localDebugMode ? <Bug className="w-4 h-4" /> : <MessageSquare className="w-4 h-4" />}
              <span>{zenDebugMode || localDebugMode ? 'Debug' : 'Chat'}</span>
              {zenDebugMode && <span className="text-xs">(Zen)</span>}
            </button>
            <button
              onClick={handleClear}
              className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-200 rounded-md transition-colors"
            >
              Clear
            </button>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-200 rounded-md transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Messages Area */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {messages.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-gray-400 mb-2">
                <Send className="w-12 h-12 mx-auto mb-4 opacity-50" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Start a conversation</h3>
              <p className="text-gray-500 max-w-sm mx-auto">
                Test different AI providers and models. Messages will appear here as you chat.
              </p>
              {currentProvider && (
                <div className="mt-4 space-y-2">
                  <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm ${
                    currentProvider.status?.includes('Cerebras') 
                      ? 'bg-orange-100 text-orange-800'
                      : 'bg-blue-100 text-blue-800'
                  }`}>
                    <span>Using: {currentProvider.provider} • {currentProvider.model}</span>
                    {currentProvider.status?.includes('Cerebras') && (
                      <Rocket className="w-3 h-3 ml-1" title="Ultra-Fast Cerebras" />
                    )}
                  </div>
                  {(zenDebugMode || localDebugMode) && (
                    <div className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-gray-100 text-gray-800 ml-2">
                      <Bug className="w-3 h-3 mr-1" />
                      Debug Mode: Raw responses will be shown {zenDebugMode && '(Zen)'}
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : (
            messages.map((msg, index) => (
              <ChatMessage key={index} message={msg} />
            ))
          )}
          {isLoading && (
            <div className="flex items-center space-x-2 text-gray-500">
              <Loader2 className="w-4 h-4 animate-spin" />
              <span className="text-sm">Thinking...</span>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input Area */}
        <div className="border-t border-gray-200 p-4">
          <form onSubmit={handleSubmit} className="flex space-x-3">
            <div className="flex-1">
              <textarea
                ref={inputRef}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Type your message... (Enter to send, Shift+Enter for new line)"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                rows={3}
                disabled={isLoading}
              />
            </div>
            <button
              type="submit"
              disabled={!message.trim() || isLoading}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <Send className="w-5 h-5" />
            </button>
          </form>
          <div className="mt-2 flex justify-between items-center text-xs text-gray-500">
            <span>
              <kbd className="px-1.5 py-0.5 bg-gray-100 rounded font-mono">Enter</kbd> to send • 
              <kbd className="px-1.5 py-0.5 bg-gray-100 rounded font-mono ml-1">Shift+Enter</kbd> for new line • 
              <kbd className="px-1.5 py-0.5 bg-gray-100 rounded font-mono ml-1">Esc</kbd> to close
            </span>
            <div className="flex items-center space-x-3">
              {currentProvider && (
                <span className="flex items-center space-x-1">
                  <span>{currentProvider.provider} • {currentProvider.model}</span>
                  {currentProvider.status?.includes('Cerebras') && (
                    <Rocket className="w-3 h-3 text-orange-500" title="Ultra-Fast Cerebras" />
                  )}
                </span>
              )}
              {(zenDebugMode || localDebugMode) && (
                <span className="flex items-center text-orange-600">
                  <Bug className="w-3 h-3 mr-1" />
                  Debug Mode {zenDebugMode && '(Zen)'}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ChatApp